import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { downloadCanvasWithWatermark } from "../chartWatermark";
import { formatNumberWithSeparators } from "../utils/formatting";
// Colors matching KPI cards specifications
export const SANKEY_COLORS = {
  revenue: "#5366FF",
  profit: "#10E8A0",
  cost: "#FF585D",
  expense: "#FFB81C",
  other: "#767C9C",
};

interface FinancialItem {
  name: string;
  value: number;
}

export interface PLData {
  revenue: FinancialItem[];
  costs: FinancialItem[];
  expenses: FinancialItem[];
  otherAccounts: FinancialItem[];
  grossProfit?: number;
  ebitda?: number;
  netProfit?: number;
}

interface SankeyChartProps {
  data?: PLData;
  onNodeClick?: (node: { name: string; value: number; type: string }) => void;
  currencySymbol?: string;
  decimalPlaces?: number;
  unidades?: "K" | "M" | "B";
}

export interface SankeyChartRef {
  downloadImage: (filename?: string) => void;
}

interface NodePosition {
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  value: number;
  percentage: number;
  color: string;
  type: string;
}

interface FlowPosition {
  path: Path2D;
  sourceName: string;
  targetName: string;
  value: number;
  percentage: number;
  color: string;
}

interface TooltipData {
  x: number;
  y: number;
  name: string;
  value: string;
  percentage: string;
  color: string;
  isFlow?: boolean;
  sourceName?: string;
  targetName?: string;
}

const ChartContainer = styled(Box)({
  position: "relative",
  width: "100%",
  backgroundColor: "#FFFFFF",
  borderRadius: 8,
  padding: "8px 0",
});

const Tooltip = styled(Box, {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ visible }) => ({
  position: "absolute",
  backgroundColor: "#FFFFFF",
  color: "#1e293b",
  padding: "12px 16px",
  borderRadius: 8,
  fontSize: "14px",
  fontFamily: '"Open Sans", sans-serif',
  pointerEvents: "none",
  zIndex: 100,
  opacity: visible ? 1 : 0,
  transition: "opacity 0.15s ease-out",
  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  minWidth: 100,
}));

const TooltipLabel = styled("div")({
  fontWeight: 600,
  marginBottom: 6,
  color: "#1e293b",
  fontSize: "14px",
});

const TooltipValue = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: "14px",
});

const TooltipColorDot = styled("div")<{ color: string }>(({ color }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: color,
}));

// Default P&L data - will be replaced by real data
const defaultPLData: PLData = {
  revenue: [
    { name: "Stream 1", value: 123500 },
    { name: "Stream 2", value: 123500 },
    { name: "Stream 3", value: 123500 },
    { name: "Stream 4", value: 123500 },
  ],
  costs: [
    { name: "Cost Component 1", value: 87700 },
    { name: "Cost Component 2", value: 23400 },
    { name: "Cost Component 3", value: 12300 },
  ],
  expenses: [
    { name: "Expense 1", value: 65400 },
    { name: "Expense 2", value: 23500 },
    { name: "Expense 3", value: 23500 },
  ],
  otherAccounts: [
    { name: "Depreciación", value: 12000 },
    { name: "Amortización", value: 8000 },
    { name: "Intereses", value: 5000 },
  ],
  ebitda: 50000,
};

// Animation helper - easing function for smooth transitions
const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

// Interpolate between two PLData objects
const interpolatePLData = (
  from: PLData,
  to: PLData,
  progress: number,
): PLData => {
  const lerp = (a: number, b: number) => a + (b - a) * progress;

  const interpolateItems = (
    fromItems: FinancialItem[],
    toItems: FinancialItem[],
  ): FinancialItem[] => {
    const maxLen = Math.max(fromItems.length, toItems.length);
    const result: FinancialItem[] = [];
    for (let i = 0; i < maxLen; i++) {
      const fromItem = fromItems[i] || {
        name: toItems[i]?.name || "",
        value: 0,
      };
      const toItem = toItems[i] || { name: fromItems[i]?.name || "", value: 0 };
      result.push({
        name: toItem.name,
        value: lerp(fromItem.value, toItem.value),
      });
    }
    return result;
  };

  // Interpolate grossProfit if both values are provided
  const grossProfit =
    from.grossProfit !== undefined && to.grossProfit !== undefined
      ? lerp(from.grossProfit, to.grossProfit)
      : to.grossProfit;

  // Interpolate ebitda if both values are provided
  const ebitda =
    from.ebitda !== undefined && to.ebitda !== undefined
      ? lerp(from.ebitda, to.ebitda)
      : to.ebitda;

  // Interpolate netProfit if both values are provided
  const netProfit =
    from.netProfit !== undefined && to.netProfit !== undefined
      ? lerp(from.netProfit, to.netProfit)
      : to.netProfit;

  return {
    revenue: interpolateItems(from.revenue, to.revenue),
    costs: interpolateItems(from.costs, to.costs),
    expenses: interpolateItems(from.expenses, to.expenses),
    otherAccounts: interpolateItems(from.otherAccounts, to.otherAccounts),
    grossProfit,
    ebitda,
    netProfit,

    //   otherAccounts: {
    //     depreciation: lerp(
    //       from.otherAccounts.depreciation || 0,
    //       to.otherAccounts.depreciation || 0
    //     ),
    //     amortization: lerp(
    //       from.otherAccounts.amortization || 0,
    //       to.otherAccounts.amortization || 0
    //     ),
    //     interests: lerp(
    //       from.otherAccounts.interests || 0,
    //       to.otherAccounts.interests || 0
    //     ),
    //     tax: lerp(from.otherAccounts.tax || 0, to.otherAccounts.tax || 0),
    //     other: lerp(from.otherAccounts.other || 0, to.otherAccounts.other || 0),
    //   },
  };
};

export const SankeyChart = forwardRef<SankeyChartRef, SankeyChartProps>(
  function SankeyChart(
    {
      data = defaultPLData,
      onNodeClick,
      currencySymbol = "$",
      decimalPlaces = 0,
      unidades = "K",
    },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [nodePositions, setNodePositions] = useState<NodePosition[]>([]);
    const [flowPositions, setFlowPositions] = useState<FlowPosition[]>([]);
    const [tooltip, setTooltip] = useState<TooltipData | null>(null);
    const [containerWidth, setContainerWidth] = useState(1200);
    const [hoveredFlowIndex, setHoveredFlowIndex] = useState<number | null>(
      null,
    );

    // Animation state
    const [animatedData, setAnimatedData] = useState<PLData>(data);
    const prevDataRef = useRef<PLData>(data);
    const animationRef = useRef<number | null>(null);

    // Expose download method
    useImperativeHandle(ref, () => ({
      downloadImage: (filename = "sankey-chart") => {
        if (!canvasRef.current) return;
        downloadCanvasWithWatermark(canvasRef.current, filename);
      },
    }));

    // Animate data changes
    useEffect(() => {
      const prevData = prevDataRef.current;
      const startTime = performance.now();
      const duration = 600; // Animation duration in ms

      // Cancel any ongoing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);

        const interpolated = interpolatePLData(prevData, data, easedProgress);
        setAnimatedData(interpolated);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          prevDataRef.current = data;
          animationRef.current = null;
        }
      };

      animationRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }, [data]);

    // Use values directly from backend
    const grossProfit = animatedData.grossProfit || 0;
    const operatingProfit = animatedData.ebitda || 0;
    const netProfit = animatedData.netProfit || 0;

    // Calculate total revenue for scaling purposes
    const totalRevenue = animatedData.revenue
      .filter((item) => item.value > 0)
      .reduce((sum, item) => sum + item.value, 0);

    const formatValue = useCallback(
      (value: number): string => {
        // Values are already scaled by backend per selected unidad; just append suffix
        const suffix = unidades || "";
        return `${currencySymbol}${formatNumberWithSeparators(
          value,
          decimalPlaces,
        )}${suffix}`;
      },
      [unidades, currencySymbol, decimalPlaces],
    );

    // Resize observer for responsive canvas
    useEffect(() => {
      if (!containerRef.current) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width - 32; // subtract padding
          setContainerWidth(Math.max(width, 600));
        }
      });

      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
      if (!canvasRef.current || totalRevenue === 0) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Calculate dynamic height based on content
      const width = containerWidth;
      const colScale = width / 1500; // Adjusted to fit all 8 columns + labels

      // Calculate required height based on data
      const costCount = animatedData.costs.filter((i) => i.value > 0).length;
      const expenseCount = animatedData.expenses.filter(
        (i) => i.value > 0,
      ).length;
      const otherCount = animatedData.otherAccounts.filter(
        (item) => item.value && item.value > 0,
      ).length;
      const maxItems = Math.max(
        animatedData.revenue.length,
        costCount + 1,
        expenseCount + 1,
        otherCount + 1,
      );
      const minHeight = 500;
      const calculatedHeight = Math.max(minHeight, maxItems * 100 + 150);
      const height = Math.min(calculatedHeight, 700);

      // Set canvas size with device pixel ratio for sharp rendering
      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);

      ctx.clearRect(0, 0, width, height);

      const colors = SANKEY_COLORS;
      const nodes: NodePosition[] = [];
      const flows: FlowPosition[] = [];

      // Scale columns - adjusted to fit all breakdown levels within container
      // Format: [revenue items, total revenue, gross profit/total costs, cost items, EBITDA/total expenses, expense items, net profit/total otras cuentas, otras cuentas items]
      const baseColumns = [100, 320, 540, 700, 860, 1020, 1180, 1340];
      const cols = baseColumns.map((c) => c * colScale);
      const nodeWidth = Math.max(18, 24 * colScale);
      const fontSize = Math.max(11, 14 * colScale);
      const smallFontSize = Math.max(10, 12 * colScale);

      // Create path for flow and store for hit detection
      const createFlowPath = (
        x1: number,
        y1: number,
        h1: number,
        x2: number,
        y2: number,
        h2: number,
      ): Path2D => {
        const path = new Path2D();
        path.moveTo(x1, y1);
        path.bezierCurveTo(
          x1 + (x2 - x1) / 2,
          y1,
          x1 + (x2 - x1) / 2,
          y2,
          x2,
          y2,
        );
        path.lineTo(x2, y2 + h2);
        path.bezierCurveTo(
          x1 + (x2 - x1) / 2,
          y2 + h2,
          x1 + (x2 - x1) / 2,
          y1 + h1,
          x1,
          y1 + h1,
        );
        path.closePath();
        return path;
      };

      const drawFlow = (
        x1: number,
        y1: number,
        h1: number,
        x2: number,
        y2: number,
        h2: number,
        color: string,
        alpha = 0.5,
        sourceName = "",
        targetName = "",
        value = 0,
        percentage = 0,
      ) => {
        const path = createFlowPath(x1, y1, h1, x2, y2, h2);

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = color;
        ctx.fill(path);
        ctx.restore();

        // Store flow for hit detection
        if (sourceName && targetName) {
          flows.push({
            path,
            sourceName,
            targetName,
            value,
            percentage,
            color,
          });
        }
      };

      const maxValue = totalRevenue;
      const padding = { top: 60, bottom: 60 };
      const availableHeight = height - padding.top - padding.bottom;
      const maxNodeHeight = availableHeight * 0.7;
      const scale = (v: number) => Math.max((v / maxValue) * maxNodeHeight, 8);
      const minNodeGap = 10;

      // Smarter vertical distribution that keeps items within bounds
      const distributeInRange = (
        items: FinancialItem[],
        startY: number,
        endY: number,
      ) => {
        const heights = items.map((i) => scale(i.value));
        const totalH = heights.reduce((s, h) => s + h, 0);
        const availableSpace = endY - startY - totalH;
        const gapSize =
          items.length > 1
            ? Math.min(availableSpace / (items.length + 1), 20)
            : 0;

        let y = startY + gapSize;
        return items.map((item, i) => {
          const pos = { ...item, y, height: heights[i] };
          y += heights[i] + gapSize;
          return pos;
        });
      };

      // === PHASE 1: Draw all flows first ===

      const revenueItems = animatedData.revenue.filter((i) => i.value > 0);
      const revenueH = scale(totalRevenue);

      // Center the main flow vertically
      const centerY = padding.top + (availableHeight - revenueH) / 2;
      const revenueY = Math.max(padding.top + 20, centerY);

      // Distribute revenue items to match revenue node
      const revenueEndY = revenueY + revenueH;
      const revenueNodes = distributeInRange(
        revenueItems,
        revenueY - 30,
        revenueEndY + 30,
      );

      // Flows from revenue items to total
      let flowY = revenueY;
      revenueNodes.forEach((node) => {
        const fh = scale(node.value);
        const pct = (node.value / totalRevenue) * 100;
        drawFlow(
          cols[0] + nodeWidth,
          node.y,
          node.height,
          cols[1],
          flowY,
          fh,
          colors.revenue,
          0.35,
          node.name,
          "Ingresos",
          node.value,
          pct,
        );
        flowY += fh;
      });

      // Gross Profit flows
      if (grossProfit > 0) {
        const gpY = revenueY - 40 * colScale;
        const gpH = scale(grossProfit);
        const gpPct = (grossProfit / totalRevenue) * 100;
        drawFlow(
          cols[1] + nodeWidth,
          revenueY,
          gpH,
          cols[2],
          gpY,
          gpH,
          colors.profit,
          0.5,
          "Ingresos",
          "Utilidad Bruta",
          grossProfit,
          gpPct,
        );

        // Total Costs flow
        const costItems = animatedData.costs.filter((i) => i.value > 0);
        if (costItems.length > 0) {
          const totalCosts = costItems.reduce(
            (sum, item) => sum + item.value,
            0,
          );
          const totalCostsH = scale(totalCosts);
          const costsTotalY = revenueY + gpH + minNodeGap;

          // Flow from Revenue to Total Costs
          drawFlow(
            cols[1] + nodeWidth,
            revenueY + gpH,
            totalCostsH,
            cols[2],
            costsTotalY,
            totalCostsH,
            colors.cost,
            0.5,
            "Ingresos",
            "Costos Totales",
            totalCosts,
            (totalCosts / totalRevenue) * 100,
          );

          // Breakdown: Total Costs to individual items
          const costEndY = Math.min(
            height - padding.bottom,
            costsTotalY + totalCostsH + 150,
          );
          const costNodes = distributeInRange(
            costItems,
            costsTotalY + 10,
            costEndY,
          );
          let costBreakdownY = costsTotalY;
          costNodes.forEach((item) => {
            const pct = (item.value / totalRevenue) * 100;
            drawFlow(
              cols[2] + nodeWidth,
              costBreakdownY,
              scale(item.value),
              cols[3],
              item.y,
              item.height,
              colors.cost,
              0.35,
              "Costos Totales",
              item.name,
              item.value,
              pct,
            );
            costBreakdownY += scale(item.value);
          });
        }

        // EBITDA flows
        if (operatingProfit > 0) {
          const opY = gpY - 40 * colScale;
          const opH = scale(operatingProfit);
          const opPct = (operatingProfit / totalRevenue) * 100;
          drawFlow(
            cols[2] + nodeWidth,
            gpY,
            opH,
            cols[4],
            opY,
            opH,
            colors.profit,
            0.5,
            "Utilidad Bruta",
            "EBITDA",
            operatingProfit,
            opPct,
          );

          // Total Expenses flow
          const expItems = animatedData.expenses.filter((i) => i.value > 0);
          if (expItems.length > 0) {
            const totalExpenses = expItems.reduce(
              (sum, item) => sum + item.value,
              0,
            );
            const totalExpensesH = scale(totalExpenses);
            const expensesTotalY = gpY + opH + minNodeGap;

            // Flow from Gross Profit to Total Expenses
            drawFlow(
              cols[2] + nodeWidth,
              gpY + opH,
              totalExpensesH,
              cols[4],
              expensesTotalY,
              totalExpensesH,
              colors.expense,
              0.5,
              "Utilidad Bruta",
              "Gastos Totales",
              totalExpenses,
              (totalExpenses / totalRevenue) * 100,
            );

            // Breakdown: Total Expenses to individual items
            const expEndY = Math.min(
              height - padding.bottom,
              expensesTotalY + totalExpensesH + 150,
            );
            const expNodes = distributeInRange(
              expItems,
              expensesTotalY + 10,
              expEndY,
            );
            let expBreakdownY = expensesTotalY;
            expNodes.forEach((item) => {
              const pct = (item.value / totalRevenue) * 100;
              drawFlow(
                cols[4] + nodeWidth,
                expBreakdownY,
                scale(item.value),
                cols[5],
                item.y,
                item.height,
                colors.expense,
                0.35,
                "Gastos Totales",
                item.name,
                item.value,
                pct,
              );
              expBreakdownY += scale(item.value);
            });
          }

          // Net Profit flows
          if (netProfit > 0) {
            const npY = opY - 40 * colScale;
            const npH = scale(netProfit);
            const npPct = (netProfit / totalRevenue) * 100;
            drawFlow(
              cols[4] + nodeWidth,
              opY,
              npH,
              cols[6],
              npY,
              npH,
              colors.profit,
              0.5,
              "EBITDA",
              "Utilidad Neta",
              netProfit,
              npPct,
            );

            // Total Other Accounts flow
            const otherItems = animatedData.otherAccounts.filter(
              (item) => item.value > 0,
            );

            if (otherItems.length > 0) {
              const totalOther = otherItems.reduce(
                (sum, item) => sum + item.value,
                0,
              );
              const totalOtherH = scale(totalOther);
              const otherTotalY = opY + npH + minNodeGap;

              // Flow from EBITDA to Total Otras cuentas
              drawFlow(
                cols[4] + nodeWidth,
                opY + npH,
                totalOtherH,
                cols[6],
                otherTotalY,
                totalOtherH,
                colors.other,
                0.5,
                "EBITDA",
                "Otras cuentas",
                totalOther,
                (totalOther / totalRevenue) * 100,
              );

              // Breakdown: Total Otras cuentas to individual items
              const otherEndY = height - padding.bottom - 20;
              const otherNodes = distributeInRange(
                otherItems,
                otherTotalY + 10,
                otherEndY,
              );
              let otherBreakdownY = otherTotalY;
              otherNodes.forEach((item) => {
                const pct = (item.value / totalRevenue) * 100;
                drawFlow(
                  cols[6] + nodeWidth,
                  otherBreakdownY,
                  scale(item.value),
                  cols[7],
                  item.y,
                  item.height,
                  colors.other,
                  0.35,
                  "Otras cuentas",
                  item.name,
                  item.value,
                  pct,
                );
                otherBreakdownY += scale(item.value);
              });
            }
          } else {
            // If no net profit, still show other accounts with more space
            const otherItems = animatedData.otherAccounts.filter(
              (item) => item.value > 0,
            );

            if (otherItems.length > 0) {
              const totalOther = otherItems.reduce(
                (sum, item) => sum + item.value,
                0,
              );
              const totalOtherH = scale(totalOther);
              const otherTotalY = opY + minNodeGap;

              // Flow from EBITDA to Total Otras cuentas
              drawFlow(
                cols[4] + nodeWidth,
                opY,
                totalOtherH,
                cols[6],
                otherTotalY,
                totalOtherH,
                colors.other,
                0.5,
                "EBITDA",
                "Otras cuentas",
                totalOther,
                (totalOther / totalRevenue) * 100,
              );

              // Breakdown: Total Otras cuentas to individual items
              const otherEndY = height - padding.bottom - 20;
              const otherNodes = distributeInRange(
                otherItems,
                otherTotalY + 10,
                otherEndY,
              );
              let otherBreakdownY = otherTotalY;
              otherNodes.forEach((item) => {
                const pct = (item.value / totalRevenue) * 100;
                drawFlow(
                  cols[6] + nodeWidth,
                  otherBreakdownY,
                  scale(item.value),
                  cols[7],
                  item.y,
                  item.height,
                  colors.other,
                  0.35,
                  "Otras cuentas",
                  item.name,
                  item.value,
                  pct,
                );
                otherBreakdownY += scale(item.value);
              });
            }
          }
        }
      }

      // === PHASE 2: Draw nodes and labels on top ===

      const drawNode = (
        x: number,
        y: number,
        h: number,
        color: string,
        label: string,
        value: number,
        align: "left" | "right" = "left",
        pct: number,
        type: string,
      ) => {
        // Draw node rectangle
        ctx.fillStyle = color;
        ctx.fillRect(x, y, nodeWidth, h);

        // Draw label background for better readability
        const textX = align === "left" ? x + nodeWidth + 8 : x - 8;
        ctx.textAlign = align;

        // Main label
        ctx.fillStyle = "#1e293b";
        ctx.font = `600 ${fontSize}px "Open Sans", sans-serif`;
        ctx.fillText(label, textX, y + h / 2 - 4);

        // Value and percentage
        ctx.font = `400 ${smallFontSize}px "Open Sans", sans-serif`;
        ctx.fillStyle = "#64748b";
        const valueText = `${formatValue(value)} (${formatNumberWithSeparators(
          pct,
          1,
        )}%)`;
        ctx.fillText(valueText, textX, y + h / 2 + 12);

        // Store node position for hover detection
        nodes.push({
          x,
          y,
          width: nodeWidth,
          height: h,
          name: label,
          value,
          percentage: pct,
          color,
          type,
        });
      };

      // Revenue items (Col 0)
      revenueNodes.forEach((item) => {
        drawNode(
          cols[0],
          item.y,
          item.height,
          colors.revenue,
          item.name,
          item.value,
          "right",
          (item.value / totalRevenue) * 100,
          "revenue",
        );
      });

      // Total Revenue (Col 1)
      drawNode(
        cols[1],
        revenueY,
        revenueH,
        colors.revenue,
        "Ingresos",
        totalRevenue,
        "left",
        100,
        "total",
      );

      // Gross Profit and Total Costs (Col 2)
      if (grossProfit > 0) {
        const gpY = revenueY - 40 * colScale;
        const gpH = scale(grossProfit);
        drawNode(
          cols[2],
          gpY,
          gpH,
          colors.profit,
          "Utilidad Bruta",
          grossProfit,
          "left",
          (grossProfit / totalRevenue) * 100,
          "profit",
        );

        const costItems = data.costs.filter((i) => i.value > 0);
        if (costItems.length > 0) {
          const totalCosts = costItems.reduce(
            (sum, item) => sum + item.value,
            0,
          );
          const totalCostsH = scale(totalCosts);
          const costsTotalY = revenueY + gpH + minNodeGap;

          // Total Costs node
          drawNode(
            cols[2],
            costsTotalY,
            totalCostsH,
            colors.cost,
            "Costos",
            totalCosts,
            "left",
            (totalCosts / totalRevenue) * 100,
            "cost",
          );

          // Individual cost breakdown (Col 3)
          const costEndY = Math.min(
            height - padding.bottom,
            costsTotalY + totalCostsH + 150,
          );
          const costNodes = distributeInRange(
            costItems,
            costsTotalY + 10,
            costEndY,
          );
          costNodes.forEach((item) => {
            drawNode(
              cols[3],
              item.y,
              item.height,
              colors.cost,
              item.name,
              item.value,
              "left",
              (item.value / totalRevenue) * 100,
              "cost",
            );
          });
        }

        // EBITDA and Total Expenses (Col 4)
        if (operatingProfit > 0) {
          const opY = gpY - 40 * colScale;
          const opH = scale(operatingProfit);
          drawNode(
            cols[4],
            opY,
            opH,
            colors.profit,
            "EBITDA",
            operatingProfit,
            "left",
            (operatingProfit / totalRevenue) * 100,
            "profit",
          );

          const expItems = data.expenses.filter((i) => i.value > 0);
          if (expItems.length > 0) {
            const totalExpenses = expItems.reduce(
              (sum, item) => sum + item.value,
              0,
            );
            const totalExpensesH = scale(totalExpenses);
            const expensesTotalY = gpY + opH + minNodeGap;

            // Total Expenses node
            drawNode(
              cols[4],
              expensesTotalY,
              totalExpensesH,
              colors.expense,
              "Gastos",
              totalExpenses,
              "left",
              (totalExpenses / totalRevenue) * 100,
              "expense",
            );

            // Individual expense breakdown
            const expEndY = Math.min(
              height - padding.bottom,
              expensesTotalY + totalExpensesH + 150,
            );
            const expNodes = distributeInRange(
              expItems,
              expensesTotalY + 10,
              expEndY,
            );
            expNodes.forEach((item) => {
              drawNode(
                cols[5],
                item.y,
                item.height,
                colors.expense,
                item.name,
                item.value,
                "left",
                (item.value / totalRevenue) * 100,
                "expense",
              );
            });
          }

          // Net Profit and Total Otras cuentas
          if (netProfit > 0) {
            const npY = opY - 40 * colScale;
            const npH = scale(netProfit);
            drawNode(
              cols[6],
              npY,
              npH,
              colors.profit,
              "Utilidad Neta",
              netProfit,
              "left",
              (netProfit / totalRevenue) * 100,
              "profit",
            );

            const otherItems = data.otherAccounts.filter((i) => i.value > 0);

            if (otherItems.length > 0) {
              const totalOther = otherItems.reduce(
                (sum, item) => sum + item.value,
                0,
              );
              const totalOtherH = scale(totalOther);
              const otherTotalY = opY + npH + minNodeGap;

              // Total Otras cuentas node
              drawNode(
                cols[6],
                otherTotalY,
                totalOtherH,
                colors.other,
                "Otras cuentas",
                totalOther,
                "left",
                (totalOther / totalRevenue) * 100,
                "other",
              );

              // Individual otras cuentas breakdown
              const otherEndY = height - padding.bottom - 20;
              const otherNodes = distributeInRange(
                otherItems,
                otherTotalY + 10,
                otherEndY,
              );
              otherNodes.forEach((item) => {
                drawNode(
                  cols[7],
                  item.y,
                  item.height,
                  colors.other,
                  item.name,
                  item.value,
                  "left",
                  (item.value / totalRevenue) * 100,
                  "other",
                );
              });
            }
          } else {
            // If no net profit, still show otras cuentas with more space
            const otherItems = data.otherAccounts.filter((i) => i.value > 0);

            if (otherItems.length > 0) {
              const totalOther = otherItems.reduce(
                (sum, item) => sum + item.value,
                0,
              );
              const totalOtherH = scale(totalOther);
              const otherTotalY = opY + minNodeGap;

              // Total Otras cuentas node
              drawNode(
                cols[6],
                otherTotalY,
                totalOtherH,
                colors.other,
                "Otras cuentas",
                totalOther,
                "left",
                (totalOther / totalRevenue) * 100,
                "other",
              );

              // Individual otras cuentas breakdown
              const otherEndY = height - padding.bottom - 20;
              const otherNodes = distributeInRange(
                otherItems,
                otherTotalY + 10,
                otherEndY,
              );
              otherNodes.forEach((item) => {
                drawNode(
                  cols[7],
                  item.y,
                  item.height,
                  colors.other,
                  item.name,
                  item.value,
                  "left",
                  (item.value / totalRevenue) * 100,
                  "other",
                );
              });
            }
          }
        }
      }

      setNodePositions(nodes);
      setFlowPositions(flows);
    }, [
      animatedData,
      totalRevenue,
      grossProfit,
      operatingProfit,
      netProfit,
      containerWidth,
      formatValue,
    ]);

    // Re-render flows when hover state changes
    useEffect(() => {
      if (!canvasRef.current || flowPositions.length === 0) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;

      // Clear and redraw
      ctx.clearRect(0, 0, width * dpr, height * dpr);

      // Redraw flows with hover effect
      flowPositions.forEach((flow, index) => {
        ctx.save();
        if (hoveredFlowIndex !== null) {
          // When something is hovered: subtle highlight/dim
          ctx.globalAlpha = index === hoveredFlowIndex ? 0.65 : 0.25;
        } else {
          // Default state based on flow type
          ctx.globalAlpha = flow.color === SANKEY_COLORS.profit ? 0.5 : 0.35;
        }
        ctx.fillStyle = flow.color;
        ctx.fill(flow.path);
        ctx.restore();
      });

      // Redraw nodes on top
      nodePositions.forEach((node) => {
        ctx.fillStyle = node.color;
        const colScale = containerWidth / 1500;
        const nodeWidth = Math.max(18, 24 * colScale);
        ctx.fillRect(node.x, node.y, nodeWidth, node.height);

        // Redraw labels
        const fontSize = Math.max(11, 14 * colScale);
        const smallFontSize = Math.max(10, 12 * colScale);
        const isRightAligned = node.x < containerWidth / 2;
        const textX = isRightAligned ? node.x + nodeWidth + 8 : node.x - 8;
        ctx.textAlign = isRightAligned ? "left" : "right";

        ctx.fillStyle = "#1e293b";
        ctx.font = `600 ${fontSize}px "Open Sans", sans-serif`;
        ctx.fillText(node.name, textX, node.y + node.height / 2 - 4);

        ctx.font = `400 ${smallFontSize}px "Open Sans", sans-serif`;
        ctx.fillStyle = "#64748b";
        const valueText = `${formatValue(
          node.value,
        )} (${formatNumberWithSeparators(node.percentage, 1)}%)`;
        ctx.fillText(valueText, textX, node.y + node.height / 2 + 12);
      });
    }, [
      hoveredFlowIndex,
      flowPositions,
      nodePositions,
      containerWidth,
      formatValue,
    ]);

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x =
          ((e.clientX - rect.left) * scaleX) / (window.devicePixelRatio || 1);
        const y =
          ((e.clientY - rect.top) * scaleY) / (window.devicePixelRatio || 1);

        // Find hovered node first (higher priority)
        const hoveredNode = nodePositions.find(
          (node) =>
            x >= node.x &&
            x <= node.x + node.width &&
            y >= node.y &&
            y <= node.y + node.height,
        );

        if (hoveredNode) {
          setHoveredFlowIndex((prev) => (prev !== null ? null : prev));
          setTooltip({
            x:
              e.clientX -
              (containerRef.current?.getBoundingClientRect().left || 0) +
              15,
            y:
              e.clientY -
              (containerRef.current?.getBoundingClientRect().top || 0) -
              10,
            name: hoveredNode.name,
            value: formatValue(hoveredNode.value),
            percentage: `${formatNumberWithSeparators(
              hoveredNode.percentage,
              1,
            )}%`,
            color: hoveredNode.color,
          });
          canvas.style.cursor = "pointer";
          return;
        }

        // Check flows
        const hoveredFlowIdx = flowPositions.findIndex((flow) => {
          return ctx.isPointInPath(
            flow.path,
            x * (window.devicePixelRatio || 1),
            y * (window.devicePixelRatio || 1),
          );
        });

        if (hoveredFlowIdx !== -1) {
          const hoveredFlow = flowPositions[hoveredFlowIdx];
          setHoveredFlowIndex((prev) =>
            prev !== hoveredFlowIdx ? hoveredFlowIdx : prev,
          );
          setTooltip({
            x:
              e.clientX -
              (containerRef.current?.getBoundingClientRect().left || 0) +
              15,
            y:
              e.clientY -
              (containerRef.current?.getBoundingClientRect().top || 0) -
              10,
            name: `${hoveredFlow.sourceName} → ${hoveredFlow.targetName}`,
            value: formatValue(hoveredFlow.value),
            percentage: `${formatNumberWithSeparators(
              hoveredFlow.percentage,
              1,
            )}%`,
            color: hoveredFlow.color,
            isFlow: true,
            sourceName: hoveredFlow.sourceName,
            targetName: hoveredFlow.targetName,
          });
          canvas.style.cursor = "pointer";
          return;
        }

        setHoveredFlowIndex((prev) => (prev !== null ? null : prev));
        setTooltip(null);
        canvas.style.cursor = "default";
      },
      [nodePositions, flowPositions],
    );

    const handleMouseLeave = useCallback(() => {
      setHoveredFlowIndex(null);
      setTooltip(null);
    }, []);

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current || !onNodeClick) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const scaleX = canvasRef.current.width / rect.width;
        const scaleY = canvasRef.current.height / rect.height;
        const x =
          ((e.clientX - rect.left) * scaleX) / (window.devicePixelRatio || 1);
        const y =
          ((e.clientY - rect.top) * scaleY) / (window.devicePixelRatio || 1);

        const clickedNode = nodePositions.find(
          (node) =>
            x >= node.x &&
            x <= node.x + node.width &&
            y >= node.y &&
            y <= node.y + node.height,
        );

        if (clickedNode) {
          onNodeClick({
            name: clickedNode.name,
            value: clickedNode.value,
            type: clickedNode.type,
          });
        }
      },
      [nodePositions, onNodeClick],
    );

    return (
      <ChartContainer ref={containerRef}>
        {totalRevenue === 0 ? (
          <Box
            sx={{
              height: 400,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
              fontFamily: '"Open Sans", sans-serif',
            }}
          >
            Ingresa datos para ver la visualización
          </Box>
        ) : (
          <>
            <canvas
              ref={canvasRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
              style={{
                display: "block",
                width: "100%",
                height: "auto",
              }}
            />
            <Tooltip
              visible={!!tooltip}
              sx={{
                left: tooltip?.x || 0,
                top: tooltip?.y || 0,
              }}
            >
              {tooltip && (
                <>
                  <TooltipLabel>{tooltip.name}</TooltipLabel>
                  <TooltipValue>
                    <TooltipColorDot color={tooltip.color} />
                    <span style={{ color: "#64748b" }}>AC:</span>
                    <span style={{ fontWeight: 600 }}>
                      {tooltip.value.replace("$", "")}
                    </span>
                  </TooltipValue>
                </>
              )}
            </Tooltip>
          </>
        )}
      </ChartContainer>
    );
  },
);

SankeyChart.displayName = "SankeyChart";
